<template>
  <div
    class="profile-item"
    :class="{
      'wide': !(isMobile || isTablet),
    }"
    @click="onClick"
    @click.stop.prevent="onGlobalClick"
  >
    <div
      class="d-inline-flex mb-1"
    >
      <div
        v-if="isUserExists && isProfileExists"
        class="d-flex flex-column align-items-center"
      >
        <b-link
          v-if="!noLinks"
          :style="isLocked('customers', user && user.user_id) ? 'opacity: 0.5' : ''"
          class="position-relative mb-1"
          @click.prevent.stop="onClick"
        >
          <b-avatar
            :id="`profile-item-${uniqKey}`"
            :key="`${ukey}`"
            :variant="avatarVariant"
            :class="`border-${avatarVariant}`"
            badge
            :badge-variant="isOnline ? 'success' : 'secondary'"
            badge-offset="-2px"
            size="4rem"
            style="border: 3px solid;"
            :src="user.avatar"
          />
          <div
            v-if="user"
            class="b-flag"
          >
            <market-flag
              :market="user.country || user.market"
            />
          </div>
        </b-link>
        <span v-else class="mb-1">
          <b-avatar
            :id="`profile-item-${uniqKey}`"
            :key="`${ukey}`"
            :variant="avatarVariant"
            :class="`border-${avatarVariant}`"
            badge
            :badge-variant="isOnline ? 'success' : 'secondary'"
            badge-offset="-2px"
            size="4rem"
            style="border: 3px solid;"
            :src="user.avatar"
          />
        </span>
        <b-tooltip
          v-if="isLocked('customers', user && user.user_id)"
          :target="`profile-item-${uniqKey}`"
          placement="bottom"
          variant="warning"
          class="text-left"
        >
          {{ $t('eLt_locked', { email: isLockedByUser.email, id: isLockedByUser.by }) }}
        </b-tooltip>
      </div>
      <div class="d-flex flex-column align-items-start px-2">
        <div class="d-flex align-items-start justify-content-between w-100">
          <div>
            <b v-if="isUserExists && isProfileExists" class="pl-2 pr-1">
              <b-link
                v-if="!noLinks"
                :class="isLocked('customers', user && user.user_id) ? 'text-secondary' : `text-${nameVariant}`"
                @click.prevent.stop="onClick"
              >
                {{ user.display_name }}
              </b-link>
              <span
                v-else
                :class="isLocked('customers', user && user.user_id) ? 'text-secondary' : `text-${nameVariant}`"
              >
                {{ user.display_name }}
              </span>
            </b>

            <b v-if="user.city" class="pr-1 small font-weight-bold text-danger">{{ user.city }}</b>
          </div>

          <div class="d-flex-inline px-2">
            <b-badge v-for="(badge, i) of user.badges" :key="i" variant="warning" class="mt-1 mr-1">
              <span v-if="badge === 'top'">
                {{ $t('eProfile_top_wing') }}
              </span>
              <span v-else-if="badge === 'prime'">
                {{ $t('eProfile_plus_wing') }}
              </span>
              <span v-else-if="badge === 'vip'">
                {{ $t('eProfile_is_vip') }}
              </span>
              <span v-else>
                {{ badge }}
              </span>
            </b-badge>

            <b-badge v-if="user.deleted" variant="danger" class="mr-1">
              {{ $t('eProfile_deleted') }}
            </b-badge>

            <b-badge v-if="user.duplicates" variant="danger" class="mr-1">
              {{ $t('eProfile_duplicated') }}
            </b-badge>

            <b-badge v-if="user.clones" variant="warning" class="mr-1">
              {{ $t('eProfile_cloned') }}
            </b-badge>

            <b-badge v-if="label" :variant="labelVariant">
              {{ label }}
            </b-badge>
          </div>
        </div>

        <div
          v-if="isUserExists && canDisplay('user_id')"
          class="row small px-2"
        >
          <div class="col col-12 col-lg-12">
            <strong class="text-secondary">{{ $t('eProfile_userId') }}:</strong>
            <span>{{ user.user_id }}</span>
            <span> - </span>
            <strong class="text-secondary">{{ $t('eProfile_type') }}:</strong>
            <span class="text-capitalize">
              {{ user.type }}
            </span>
          </div>
          <div class="col col-12 col-lg-12">
            <strong class="text-secondary">{{ $t('eProfile_status') }}:</strong>
            <span class="text-capitalize">
              {{ user.status }}
            </span>
            <span> - </span>
            <strong class="text-secondary">{{ $t('eProfile_validated') }}:</strong>
            <span>{{ user.validated_at ? $dateFormat(user.validated_at, 'DD/MM/YYYY') : 'None' }}</span>
          </div>
          <div class="col col-12 col-lg-12">
            <strong class="text-secondary">{{ $t('eProfile_ranking') }}:</strong>
            <span>{{ user.ranking }}</span>
            <span> - </span>
            <strong class="text-secondary">{{ $t('eProfile_stars') }}:</strong>
            <span>{{ user.stars }}</span>
          </div>

          <div
            v-if="isUserExists && canDisplay('email') && user.email"
            class="col col-12 d-flex align-items-start mt-1"
          >
            <span class="pr-1" :class="user.email_verified ? 'text-success' : 'text-danger'">
              <font-awesome-icon v-if="user.email_verified" size="sm" :icon="['fas', 'check']" />
              <font-awesome-icon v-else size="sm" :icon="['fas', 'triangle-exclamation']" />
            </span>
            <b-link
              v-if="!noLinks"
              class="text-dark"
              @click="onEmailClick(user.email)"
            >
              {{ user.email }}
            </b-link>
            <span v-else>
              {{ user.email }}
            </span>
          </div>
          <div
            v-if="isUserExists && canDisplay('mobile') && user.mobile"
            class="col col-12 d-flex align-items-start mt-1"
          >
            <span class="pr-1" :class="user.mobile_verified ? 'text-success' : 'text-danger'">
              <font-awesome-icon v-if="user.mobile_verified" size="sm" :icon="['fas', 'check']" />
              <font-awesome-icon v-else size="sm" :icon="['fas', 'triangle-exclamation']" />
            </span>
            {{ user.mobile }}
          </div>

          <div
            v-if="isUserExists && canDisplay('city') && user.city_id"
            class="col col-12 d-flex align-items-start mt-1"
          >
            <span class="pr-1">
              <font-awesome-icon size="sm" :icon="['fas', 'location-dot']" />
            </span>
            <b-link
              v-if="!noLinks"
              class="text-dark"
              @click="onCityClick(user.city_id)"
            >
              {{ user.city }}
            </b-link>
            <span v-else>
              {{ user.city }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { isServer } from '@/mixins/helpers'

export default {
  name: 'UserProfileItem',
  props: {
    user: {
      type: Object,
      default: () => ({}),
      required: false
    },
    /*
    * Awailable values:
    * ['user_id', 'email', 'mobile', 'city']
    * */
    fields: {
      type: Array,
      default: () => ([])
    },
    label: {
      type: String,
      default: ''
    },
    labelVariant: {
      type: String,
      default: 'info'
    },
    nameVariant: {
      type: String,
      default: 'info'
    },
    noLinks: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      ukey: 0
    }
  },
  computed: {
    ...mapGetters({
      isLocked: 'shared/isLocked',
      isLockedBy: 'shared/isLockedBy',
      customersOnline: 'staffUser/customersOnline',
      customersOnlineCount: 'staffUser/customersOnlineCount',
      tableLayout: 'tableLayout',
      deviceInfo: 'device'
    }),
    isUserExists () {
      return this.user.user_id !== null
    },
    isProfileExists () {
      return this.user.user_profile_id !== null
    },
    isMobile () {
      return this.deviceInfo?.type === 'mobile'
    },
    isTablet () {
      return this.deviceInfo?.type === 'tablet'
    },
    displayMobile () {
      let mobile = ''
      if (this.user.mobile_prefix) {
        mobile += `(${this.user.mobile_prefix})`
      }
      if (this.user.mobile) {
        if (this.user.mobile_prefix) {
          mobile += ` ${this.user.mobile.replace(this.user.mobile_prefix, '')}`
        } else {
          mobile += this.user.mobile
        }
      }
      return mobile
    },
    uniqKey () {
      return Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 8)
    },
    isLockedByUser () {
      const user = this.isLockedBy('customers', this.user.user_id)
      if (user) {
        return user
      }
      return false
    },
    displayFields () {
      const def = [
        'user_id'
      ]
      return def.concat(this.fields)
    },
    status () {
      return this.user?.status || ''
    },
    avatarVariant () {
      let variant = 'info'
      if (this.isLocked('customers', this.user?.user_id)) {
        variant = 'secondary'
      } else {
        switch (this.status) {
          case 'active':
            variant = 'success'
            break
          case 'inactive':
          case 'suspended':
            variant = 'warning'
            break
          case 'deleted':
            variant = 'danger'
            break
          case 'register':
            variant = 'primary'
            break
          case 'hidden':
            variant = 'secondary'
            break
          default:
            variant = 'info'
            break
        }
      }
      return variant
    },
    isOnline () {
      return this.customersOnline?.includes(this.user?.user_id) || false
    }
  },
  methods: {
    onClick () {
      if (!isServer) {
        window.open(`/customers/${this.user.user_id}`, '_blank').focus()
      }
      // this.$openGlobal('CustomerEditForm', this.user.id, 'customers')
      this.$emit('click', this.user.user_id)
    },
    onCityClick (cityId) {
      window.open(`customers?filter[city]=${cityId}`, '_blank').focus()
    },
    onEmailClick (email) {
      window.open(`customers?filter[email]=${email}`, '_blank').focus()
    },
    onGlobalClick () {
      this.$emit('click', this.user.user_id)
    },
    canDisplay (fieldName) {
      return !!this.displayFields?.includes(fieldName)
    }
  }
}
</script>

<style>
.b-avatar .b-avatar-badge {
  font-size: 0.75rem !important;
}

.b-flag {
  display: flex;
  position: absolute;
  top: 4px;
  right: -4px;
  border: 1px solid #6c757d;
  border-radius: 4px;
  overflow: hidden;
  opacity: 0.85;
}

.profile-item .wide {
  min-width: 400px;
}

.profile-item .small {
  font-size: 0.8rem;
}
</style>
